/**
 * UC agent-state returns campaign strings (e.g. Q471); Platform catalog maps campaign_id → title.
 * Shared by Pages Functions, Presence Worker, and frontend.
 */
export function buildCampaignIdToTitleMap(
  catalog: Array<{ campaign_id: number; campaign_title: string | null }>
): Map<number, string> {
  const m = new Map<number, string>();
  for (const r of catalog) {
    const t = r.campaign_title?.trim();
    if (!t) continue;
    m.set(r.campaign_id, t);
  }
  return m;
}

/** Resolve a UC code to catalog title when the numeric part matches campaign_id (e.g. Q471 → id 471). */
export function resolveCampaignCodeToName(code: string, idToTitle: Map<number, string>): string {
  const raw = code.trim();
  if (!raw) return raw;
  const asInt = parseInt(raw, 10);
  if (Number.isFinite(asInt) && idToTitle.has(asInt)) return idToTitle.get(asInt)!;
  const stripped = raw.replace(/^Q\s*/i, '').trim();
  const n = parseInt(stripped, 10);
  if (Number.isFinite(n) && idToTitle.has(n)) return idToTitle.get(n)!;
  return raw;
}

export function formatCodesAsCampaignNames(codes: string[], idToTitle: Map<number, string>): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const c of codes) {
    const label = resolveCampaignCodeToName(c, idToTitle);
    if (seen.has(label)) continue;
    seen.add(label);
    parts.push(label);
  }
  return parts.join(', ');
}

/** Split the truncated display label from UC codes (handles "Q1, Q2…"). */
function segmentsFromCampaignDisplay(display: string): string[] {
  const t = display.trim();
  if (!t) return [];
  return t
    .split(/[,\n]/)
    .map((s) => s.replace(/\s*(?:…|\.\.\.)\s*$/, '').trim())
    .filter(Boolean);
}

/** True if any UC code on the agent resolves to this catalog title (sidebar filter match). */
export function agentBelongsToCampaignTitle(
  campaignDisplay: string,
  campaignCodes: string[] | undefined,
  selectedTitle: string,
  idToTitle: Map<number, string>
): boolean {
  if (campaignCodes?.length) {
    for (const c of campaignCodes) {
      if (resolveCampaignCodeToName(c, idToTitle) === selectedTitle) return true;
    }
  }
  for (const seg of segmentsFromCampaignDisplay(campaignDisplay)) {
    if (resolveCampaignCodeToName(seg, idToTitle) === selectedTitle) return true;
  }
  return false;
}
